import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { anotarSkills, desbloquearSkill, SkillNoDisponibleError } from '@/engine/skills';
import { useCharacterStore } from '@/engine/state';
import { FullscreenPage } from '@/components/ui';
import { SkillNode } from './SkillNode';

type RamaId = 'guardian' | 'druida' | 'estratega';

const RAMAS: Array<{ id: RamaId; nombre: string; lema: string }> = [
  { id: 'guardian', nombre: 'Guardián', lema: 'Protegés lo que otros dejan caer.' },
  { id: 'druida', nombre: 'Druida', lema: 'La savia también es sangre.' },
  { id: 'estratega', nombre: 'Estratega', lema: 'Ves el tablero antes de que se toque.' },
];

export function SkillTreeView() {
  const puntos = useCharacterStore((s) => s.personaje.puntosSkillDisponibles);
  const skillsList = useCharacterStore((s) => s.personaje.skillsDesbloqueadas.join(','));
  const anotadas = useMemo(() => anotarSkills(), [skillsList, puntos]);
  const [error, setError] = useState<string | null>(null);
  const [ramaActiva, setRamaActiva] = useState<RamaId>('guardian');

  const intentarDesbloquear = (id: string) => {
    try {
      desbloquearSkill(id);
      setError(null);
    } catch (e) {
      if (e instanceof SkillNoDisponibleError) setError(e.message);
      else throw e;
    }
  };

  return (
    <FullscreenPage
      titulo="Habilidades"
      subtitulo={`Puntos disponibles: ${puntos}`}
      accion={
        error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fluid-xs text-ember-400 font-ui max-w-xs clamp-2"
          >
            {error}
          </motion.p>
        )
      }
    >
      {/* Tabs de rama: visibles en todas las pantallas, permiten elegir rama activa en <lg */}
      <div className="lg:hidden mb-4 flex gap-1.5 shrink-0">
        {RAMAS.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setRamaActiva(r.id)}
            className={`flex-1 px-3 py-2 rounded text-center fluid-xs font-display uppercase tracking-widest border transition-colors ${
              ramaActiva === r.id
                ? 'border-ember-600 text-ember-300 bg-ash-950'
                : 'border-ash-800 text-ash-500 hover:text-ash-200'
            }`}
          >
            {r.nombre}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
        {RAMAS.map((rama) => {
          const skillsRama = anotadas
            .filter((a) => a.skill.rama === rama.id)
            .sort(
              (a, b) =>
                a.skill.tier - b.skill.tier || a.skill.posicion.y - b.skill.posicion.y,
            );
          const ocultaEnMobile = rama.id !== ramaActiva;
          return (
            <section
              key={rama.id}
              className={ocultaEnMobile ? 'hidden lg:block' : ''}
            >
              <header className="mb-2 hidden lg:block">
                <h2 className="text-display fluid-base text-ash-100">{rama.nombre}</h2>
                <p className="fluid-xs italic text-ash-500 font-ui">{rama.lema}</p>
              </header>
              <div className="space-y-2">
                {skillsRama.map((anot) => (
                  <SkillNode
                    key={anot.skill.id}
                    anotada={anot}
                    onClick={() => intentarDesbloquear(anot.skill.id)}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </FullscreenPage>
  );
}
